import { Path, Point, Layer, Size } from 'paper/dist/paper-core';
import EventEmitter from 'events';
import Sprite from './Sprite';
import sample from 'lodash/sample';
import random from 'lodash/random';

import fire from '../../assets/fire.png';

class Car extends EventEmitter {
    constructor({ 
        name, 
        team,
        sprite, 
        path, 
        speed = 30, 
        laps = 10, 
        shouldCrash = false
    }){
        super();

        this.ACCELERATION = 0.8;
        this.BRAKE_FRICTION = 0.9;
        this.COASTING_FRICTION = 0.98;
        this.SLIDING_FRICTION = 3.8;
        this.ROTATION_ON_EXIT = 60;

        this._name = name;
        this._team = team;
        this._laps = laps;
        this._speed = speed;

        this._car = null;
        this._fire = null;
        this._path = path;
        this._sprite = sprite;
        this._layer = null; 
        this._shouldCrash = shouldCrash;
        
        this.setDefaults();
        this.initialize();
    }

    async initialize() {
        this._layer = new Layer();
        
        this._car = await new Promise( (resolve, reject) => {
            this._layer.importSVG(this._sprite.src, (path, svg) => {
                resolve(path);
            });
        });

        this._car.applyMatrix = false;

        this._fire = new Sprite(fire, new Size(128, 64));
        this._fire.pivot = new Point(this._fire.bounds.width, this._fire.bounds.height / 2);
        this._fire.visible = false;
        
        this.renderCar(this._position);
    }

    setDefaults(){
        this._velocity = new Point(0, 0);
        this._velocity.length = 0;
        this._throttle = 0;
        this._position = this._path.getPointAt(this._path.length);
        this._rotation = 0;
        this._rotationExit = 0; 
        this._elapsed = 0;
        this._elapsedExit = 0; 
        this._pathExit = null;
        this._lastPoint = null;

        this._startTime = 0;
        this._currentTime = null;
        this._currentSector = 0;
        this._currentLap = 0;
        this._progressLap = 0;
        this._progressLaps = 0;

        this._isOnTrack = true;
        this._isFinished = false;
        this._hasStopped = false;

        this._accident = sample(['fire', 'crash']);
        this._accidentAtLap = random(1, this._laps - 1);
        this._crashAtSector = sample([20,40,60]);
    }

    reset() {
        this.setDefaults();
        this.renderCar(this._position);
    }

    remove() {
        this._layer.remove();
    }

    accelerate() {
        this._throttle = this.ACCELERATION;
        this._startTime = performance.now();
    }

    brake() {
        this._throttle = 0;
    }

    getElapsedTime(){
        var endTime = performance.now();
        var timeDiff = endTime - this._startTime; //in ms 
        
        // strip the ms 
        timeDiff /= 1000; 
        
        // return seconds 
        return timeDiff;
    }

    calculateSpeed() {
        if (this._throttle) {
            this._velocity.length += this._throttle;

            if (this._velocity.length > this._speed) {
                this._velocity.length = this._speed;
            }
        } else if( this._hasStopped ) {
            this._velocity.length *= this.COASTING_FRICTION;
        } else {
            this._velocity.length *= this.BRAKE_FRICTION;
        }
    }

    calculateCrash(point){
        this._layer.activate();

        const offset = this._path.getOffsetOf(point);
        const offset_prev = this._path.getOffsetOf(this._position);
        const offset_mid = (offset + offset_prev) / 2;

        const point_angle = this._path.getTangentAt(offset).angle;
        const prev_point_angle = this._path.getTangentAt(offset_mid).angle;
        let direction = -1;

        if (parseFloat(prev_point_angle) > parseFloat(point_angle)) {
            direction = 1;
        }

        const normalAtPosition = this._path.getNormalAt(offset).multiply(1000 * direction);
        const normalAtPoint = this._path.getNormalAt(offset_prev).multiply(1000 * direction);

        const l1 = this.drawLine(point, point.add(normalAtPosition), null, 1);
        const l2 = this.drawLine(this._path.getPointAt(offset_prev), this._path.getPointAt(offset_prev).add(normalAtPoint), '#2895FF', 1);

        const intersection = l1.getIntersections(l2);

        if (intersection.length > 0 && this._currentLap === this._accidentAtLap && this._currentSector === this._crashAtSector) {
            this._pathExit = this.drawLine(
                point, 
                point.add(this._velocity.multiply(50)), 
                null, 
                0
            );
            this._elapsedExit = 0;
            this._rotationExit = this.ROTATION_ON_EXIT;
            this._throttle = 0;
            this._isOnTrack = false;
            this._currentTime = null;
        }

        l1.remove();
        l2.remove();
    }

    drawLine(p1, p2, color, size) {
        var col = color || '#AAE727';
        var s = size || 0;
        var line = new Path({
            segments: [p1, p2],
            strokeColor: col,
            strokeWidth: s
        });

        return line;
    }

    render( event ) {
        this.calculateSpeed();

        const trackOffset = this._elapsed % this._path.length;
        const trackPoint = this._path.getPointAt(trackOffset);
        const trackTangent = this._path.getTangentAt(trackOffset);
        const trackAngle = trackTangent.angle;

        this._lastPoint = trackPoint;
        this._velocity.angle = trackAngle;
        this._progressLaps = (100 / (this._path.length * this._laps)) * this._elapsed;
        this._progressLap = (100 / this._path.length) * trackOffset;

        const newSector = (Math.round( this._progressLap / 20) * 20) % 100;

        if ( this._isOnTrack && !this._isFinished && !this._hasStopped ) {
            this._currentTime = this.getElapsedTime();
        }

        if ( !this._isOnTrack && this._velocity.length > 0.1 ) {
            this.renderCrash();
            return;
        } 

        this._elapsed += this._velocity.length;

        if( this._elapsed > (this._path.length * this._laps) && this._throttle === this.ACCELERATION ) {
            this._isFinished = true;
            this.brake();
            this.emit('finished', this);
        }

        if( !this._isFinished && this._currentSector !== newSector && newSector === 0 ) {
            this._currentLap++; 
            this.emit('newlap', this);
        }

        if( !this._isFinished && this._currentSector !== newSector ) {
            this._currentSector = newSector;
            this.emit('newsector', this);
        }

        if( !this._isFinished && this._shouldCrash ) {
            if( this._accident === 'fire' ) {
                this.renderFire(event.count);
            }
    
            if( this._accident === 'crash' && this._velocity.length > 0.1 ) {
                this.calculateCrash(trackPoint);
            }
        }

        if (this._velocity.length > 0.1) {
            this.renderCar(trackPoint);
        }
    }

    renderCar(point) {
        this._rotation = this._velocity.angle;
        this._position = point;

        this.updateCarPosition();
    }

    renderFire(frame){
        if( !this._fire ) return;

        if( this._currentLap === this._accidentAtLap && this._currentSector === this._crashAtSector ) {
            this._fire.visible = true;
            this.brake();
            this._hasStopped = true;
            this._currentTime = null;
        }

        // Only show fire when slowing down
        if( this._velocity.length <= this._speed / 4 ) {
            this._fire.visible = false;
        }

        // Slow framerate down
        if (frame % 3 === 0) {
            this._fire.next();
        }
    }

    renderCrash() {
        const trackOffsetExit = this._elapsedExit % this._pathExit.length;
        const trackPointExit = this._pathExit.getPointAt(trackOffsetExit);

        this._rotationExit *= this.BRAKE_FRICTION;
        this._elapsedExit += this._velocity.length;
        this._rotation = parseFloat(this._rotation) + this._rotationExit;
        this._position = trackPointExit;

        this.updateCarPosition();
    }

    updateCarPosition() {
        if( !this._car ) return;

        this._car.position = new Point(this._position);
        this._car.rotation = this._rotation;

        this._fire.position = this._position;
        this._fire.rotation = this._velocity.angle;
    }

    getDriverStatus() {
        return {
            name: this._name,
            team: this._team,
            color: this._sprite.color,
            time: this._currentTime,
            sector: this._currentSector,
            progress: this._progressLaps,
            lap: this._currentLap,
            isFinished: this._isFinished,
            hasStopped: this._hasStopped || !this._isOnTrack
        };
    }

    restartAfterCrash() {
        this._rotation = this._velocity.angle;
        this._position = this._lastPoint;
        this.updateCarPosition();
    }
}


export default Car;